import {
    Box,
    Tab,
    Tabs,
    Toolbar,
    useMediaQuery,
    IconButton,
    Tooltip,
    Typography
} from "@mui/material";

import {
    CheckBoxOutlineBlankOutlined,
    ContactMailOutlined,
    DirectionsCarFilledOutlined,
    HailOutlined,
    SendToMobileOutlined,
    TransferWithinAStationOutlined,
    CloseOutlined,
} from "@mui/icons-material";

import TransferVehicleComponent from "../../privateManagerApp/customerHelp/options/TransferVehicleComponent";
import TransferDriverComponent from "../../privateManagerApp/customerHelp/options/TransferDriverComponent";
import CustomerHelpDeskComponent from "../../privateManagerApp/customerHelp/options/CustomerHelpDeskComponent";
import DeviceCommandComponent from "../../privateManagerApp/customerHelp/options/DeviceCommandComponent";
import RecordVehicleDriverComponent from "../../privateManagerApp/customerHelp/options/RecordVehicleDriverComponent";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useRecoilValue } from "recoil";
import { PermissionsType, UserType } from "../../../types/types";
import { userAtom } from "../../../atoms/atoms";
import { MENUS_ITENS } from "../../../consts/consts";
import CheckListComponent from "../../privateManagerApp/customerHelp/options/CheckListComponent";

const HelpPage = ({ isHiddenMenu }: { isHiddenMenu: boolean }) => {
    // atoms
    const getCurrentUser = useRecoilValue<UserType>(userAtom);

    const [step, setStep] = useState<number>(0);

    const isMobile = useMediaQuery("(max-width:650px)");

    const isGridTemplateStep = useMemo(() => {
        if (getCurrentUser.configs.isDriver) return step === 0;
        if (getCurrentUser.customer?.useCpf) return step === 0;
        return [2, 3, 5].includes(step);
    }, [getCurrentUser, step]);

    const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        setStep(newValue);
    };

    const getItemPermission = useCallback((id: number) => {
        return getCurrentUser.permissions.find((permission: PermissionsType) => {
            if (permission.optionItem.id === id) {
                const { optionItem, ...rest } = permission;
                return rest;
            };
        });
    }, [getCurrentUser.permissions]);

    const permissionToTransferVehicle = useMemo(() => getItemPermission(Number(MENUS_ITENS.TRANSFER_VEHICLE)), [getItemPermission]);
    const permissionToTransferDriver = useMemo(() => getItemPermission(Number(MENUS_ITENS.TRANSFER_DRIVER)), [getItemPermission]);
    const permissionToHelpDesk = useMemo(() => getItemPermission(Number(MENUS_ITENS.HELP_DESK)), [getItemPermission]);
    const permissionToCommand = useMemo(() => getItemPermission(Number(MENUS_ITENS.COMMANDS)), [getItemPermission]);
    const permissionToDriverAccess = useMemo(() => getItemPermission(Number(MENUS_ITENS.DRIVER_ACCESSES)), [getItemPermission]);
    const permissionToCheckList = useMemo(() => getItemPermission(Number(MENUS_ITENS.CHECK_LIST)), [getItemPermission]);

    const getFirstAccessibleStep = useCallback(() => {
        const permissions = [
            permissionToCheckList?.canView && getCurrentUser.configs.customerSettings?.useCheckList,
            permissionToTransferVehicle?.canView && permissionToTransferVehicle?.canCreate,
            permissionToTransferDriver?.canView && permissionToTransferDriver?.canCreate,
            permissionToHelpDesk?.canView,
            permissionToCommand?.canView && permissionToCommand?.canCreate,
            permissionToDriverAccess?.canView
        ];
        const index = permissions.findIndex(p => p === true);
        setStep(index !== -1 ? index : 0);
    }, [permissionToTransferVehicle, permissionToTransferDriver, permissionToHelpDesk, permissionToCommand, permissionToDriverAccess, permissionToCheckList, getCurrentUser.configs.customerSettings?.useCheckList]);

    useEffect(() => {
        getFirstAccessibleStep();
    }, [getFirstAccessibleStep]);

    return (
        <Box
            className="grid-box-out-container tw-flex tw-min-h-screen tw-w-full tw-items-center tw-justify-center tw-bg-transparent tw-p-0.5"
            style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                minHeight: "100vh",
                background: "transparent",
            }}
        >
            <Box
                className="grid-container support-workspace container container-fluid tw-w-full tw-overflow-hidden tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-p-2.5 tw-shadow-md"
                style={{
                    padding: 10,
                    background: "#ffff",
                    transition: "all 0.3s ease-out allow-discrete",
                    width: isMobile ? "100%" : (isHiddenMenu ? "70vw" : "65vw"),
                    borderRadius: 12,
                    position: "relative"
                }}
            >
                {!isGridTemplateStep && (
                    <Toolbar
                        className="grid-header-bar tw-mb-2 tw-mt-2 tw-rounded-xl tw-bg-gtr-sidebar tw-px-3 tw-text-white tw-shadow-sm sm:tw-px-4"
                        style={{
                            display: "flex",
                            backgroundImage: "linear-gradient(to right, #09414A , #09414A)",
                            fontFamily: "Prometo",
                            marginTop: 10,
                            marginBottom: 10,
                            borderRadius: 12,
                            color: "#ffff",
                            position: "relative",
                            width: "100%",
                            minHeight: 64,
                        }}
                    >
                        <Box
                            className="header-actions tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-3"
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                alignContent: "center",
                                fontFamily: "Prometo",
                                width: "100%"
                            }}
                        >
                            <Box className="tw-flex tw-min-w-0 tw-items-center tw-gap-3">
                                <Box className="tw-flex tw-h-10 tw-w-10 tw-flex-none tw-items-center tw-justify-center tw-rounded-xl tw-bg-white/10">
                                    <ContactMailOutlined />
                                </Box>
                                <Typography className="tw-truncate" sx={{ fontFamily: "Prometo", fontWeight: 700, fontSize: { xs: 16, sm: 18 } }}>
                                    {getCurrentUser.configs.isManager ? "Ferramentas" : "Ajuda e Suporte"}
                                </Typography>
                            </Box>
                            <Tooltip title="Fechar" arrow>
                                <IconButton
                                    aria-label="Fechar ajuda e suporte"
                                    onClick={() => {
                                        window.location.replace("/")
                                    }}
                                    sx={{
                                        color: "#ffff",
                                        transition: "background-color 0.2s ease-out, transform 0.2s ease-out",
                                        "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.12)", transform: "translateY(-1px)" },
                                    }}
                                >
                                    <CloseOutlined />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Toolbar>
                )}
                {getCurrentUser.configs.isDriver && <Box
                    className="support-content-surface tw-flex tw-min-h-0 tw-w-full tw-items-start tw-justify-center tw-overflow-visible tw-rounded-xl tw-bg-white tw-py-1"
                    role="tabpanel"
                    id={`scrollable-force-tabpanel-${step}`}
                    aria-labelledby={`scrollable-force-tab-${step}`}
                    style={{
                        width: "100%",
                        borderRadius: 5,
                        backgroundColor: "#ffff",
                        fontFamily: "Prometo",
                        minHeight: 0,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "flex-start"
                    }}
                >
                    {step === 0 && <CheckListComponent />}
                </Box>}

                {!getCurrentUser.customer?.useCpf && !getCurrentUser.configs.isDriver && <Box
                    className="support-content-surface tw-flex tw-min-h-0 tw-w-full tw-items-start tw-justify-center tw-overflow-visible tw-rounded-xl tw-bg-white tw-py-1"
                    role="tabpanel"
                    id={`scrollable-force-tabpanel-${step}`}
                    aria-labelledby={`scrollable-force-tab-${step}`}
                    style={{
                        width: "100%",
                        borderRadius: 5,
                        backgroundColor: "#ffff",
                        fontFamily: "Prometo",
                        minHeight: 0,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "flex-start"
                    }}
                >
                    {step === 0 && <TransferVehicleComponent />}
                    {step === 1 && <TransferDriverComponent />}
                    {step === 2 && <RecordVehicleDriverComponent />}
                    {step === 3 && <CustomerHelpDeskComponent />}
                    {step === 4 && <DeviceCommandComponent />}
                    {step === 5 && <CheckListComponent />}
                </Box>}

                {getCurrentUser.customer?.useCpf && <Box
                    className="support-content-surface tw-flex tw-min-h-0 tw-w-full tw-items-start tw-justify-center tw-overflow-visible tw-rounded-xl tw-bg-white tw-py-1"
                    role="tabpanel"
                    id={`scrollable-force-tabpanel-${step}`}
                    aria-labelledby={`scrollable-force-tab-${step}`}
                    style={{
                        width: "100%",
                        borderRadius: 5,
                        backgroundColor: "#ffff",
                        fontFamily: "Prometo",
                        minHeight: 0,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "flex-start"
                    }}
                >
                    {/*step === 0 && <RecordVehicleDriverComponent />*/}
                    {step === 0 && <CustomerHelpDeskComponent />}
                </Box>}

                <Toolbar
                    className="grid-header-bar tw-mt-2 tw-rounded-xl tw-bg-gtr-sidebar tw-text-white tw-shadow-sm"
                    sx={{
                        display: "flex",
                        backgroundImage: "linear-gradient(to right, #09414A , #09414A)",
                        fontFamily: "Prometo",
                        minHeight: 48,
                        borderRadius: 3,
                        mb: 1,
                        color: "#fff",
                        px: 1,
                    }}
                >
                    <Box
                        className="header-actions"
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            overflowX: "auto",
                            width: "100%",
                        }}
                    >
                        {getCurrentUser.configs.isDriver && <Tabs
                            value={step}
                            onChange={handleChange}
                            variant={isMobile ? "scrollable" : "fullWidth"}
                            scrollButtons={isMobile ? "auto" : false}
                            allowScrollButtonsMobile
                            indicatorColor="primary"
                            textColor="inherit"
                            sx={{
                                fontFamily: "Prometo",
                                color: "#fff",
                                width: "100%",
                                "& .MuiTabs-indicator": { backgroundColor: "#00B700", height: 3 },
                                "& .MuiTab-root": { minHeight: 58, borderRadius: 2, transition: "background-color 200ms ease, color 200ms ease" },
                                "& .Mui-selected": { backgroundColor: "rgba(255, 255, 255, 0.10)" },
                            }}
                        >
                            <Tab
                                icon={<CheckBoxOutlineBlankOutlined />}
                                aria-label="device-info" label={isMobile ? "" : "Checklist do veículo"} sx={{ border: "none", fontSize: 8, fontFamily: "Prometo" }}
                                disabled={!permissionToCheckList?.canView || !getCurrentUser.configs.customerSettings?.useCheckList}
                            />
                        </Tabs>}

                        {!getCurrentUser.customer?.useCpf && !getCurrentUser.configs.isDriver && <Tabs
                            value={step}
                            onChange={handleChange}
                            variant={isMobile ? "scrollable" : "fullWidth"}
                            scrollButtons={isMobile ? "auto" : false}
                            allowScrollButtonsMobile
                            indicatorColor="primary"
                            textColor="inherit"
                            sx={{
                                fontFamily: "Prometo",
                                color: "#fff",
                                width: "100%",
                                "& .MuiTabs-indicator": { backgroundColor: "#00B700", height: 3 },
                                "& .MuiTab-root": { minHeight: 58, borderRadius: 2, transition: "background-color 200ms ease, color 200ms ease" },
                                "& .Mui-selected": { backgroundColor: "rgba(255, 255, 255, 0.10)" },
                            }}
                        >
                            <Tab
                                icon={<DirectionsCarFilledOutlined />}
                                aria-label="device-info" label={isMobile ? "" : "Transferência de veículo"} sx={{ border: "none", fontSize: 8, fontFamily: "Prometo" }}
                                disabled={!permissionToTransferVehicle?.canView && !permissionToTransferVehicle?.canCreate}
                            />
                            <Tab
                                icon={<TransferWithinAStationOutlined />}
                                aria-label="device-info" label={isMobile ? "" : "Transferência de motorista"} sx={{ border: "none", fontSize: 8, fontFamily: "Prometo" }}
                                disabled={!permissionToTransferDriver?.canView && !permissionToTransferDriver?.canCreate}
                            />
                            <Tab
                                icon={<HailOutlined />}
                                aria-label="device-info" label={isMobile ? "" : "Acesso do condutor"} sx={{ border: "none", fontSize: 8, fontFamily: "Prometo" }}
                                disabled={!permissionToDriverAccess?.canView}
                            />
                            <Tab
                                icon={<ContactMailOutlined />}
                                aria-label="device-info" label={isMobile ? "" : "Atendimento"} sx={{ border: "none", fontSize: 8, fontFamily: "Prometo" }}
                                disabled={!permissionToHelpDesk?.canView}
                            />
                            <Tab
                                icon={<SendToMobileOutlined />}
                                aria-label="device-info" label={isMobile ? "" : "Envio de comando"} sx={{ border: "none", fontSize: 8, fontFamily: "Prometo" }}
                                disabled={!permissionToCommand?.canView && !permissionToCommand?.canCreate}
                            />
                            <Tab
                                icon={<CheckBoxOutlineBlankOutlined />}
                                aria-label="device-info" label={isMobile ? "" : "Checklist do veículo"} sx={{ border: "none", fontSize: 8, fontFamily: "Prometo" }}
                                disabled={!permissionToCheckList?.canView || !getCurrentUser.configs.customerSettings?.useCheckList}
                            />
                        </Tabs>}

                        {getCurrentUser.customer?.useCpf && <Tabs
                            value={step}
                            onChange={handleChange}
                            variant="standard"
                            indicatorColor="primary"
                            textColor="inherit"
                            aria-label="icon tabs info"
                            className="tab-container"
                            sx={{
                                fontSize: "18px",
                                fontFamily: "Prometo",
                                color: "#ffff",
                                "& .MuiTabs-indicator": { backgroundColor: "#00B700", height: 3 },
                                "& .Mui-selected": { backgroundColor: "rgba(255, 255, 255, 0.10)" },
                            }}
                        >
                            {/*<Tab
                                icon={<HailOutlined />}
                                aria-label="device-info"
                                label="Autolotação de motoristas"
                                sx={{ border: "none", fontSize: 8, fontFamily: "Prometo" }}
                            />*/}
                            <Tab
                                icon={<ContactMailOutlined />}
                                aria-label="device-info"
                                label="Atendimento"
                                sx={{ border: "none", fontSize: 8, fontFamily: "Prometo" }}
                            />
                        </Tabs>}
                    </Box>
                </Toolbar>
            </Box>
        </Box>
    );
};

export default HelpPage;
